import { useEffect, useRef, useState } from 'react';
import { callEdgeFunction } from './supabaseClient';
import {
  checkStatusLabel,
  formatPerson,
  formatSimpraMoney,
  unixToLocale,
  pickPropertyDiscountIdFromCheck,
  resolveGroupPropertyDiscountId,
} from './simpraCheck';
import './QRCodesPage.css';

function shortId(id) {
  if (!id) return '—';
  const s = String(id);
  return s.length > 12 ? `${s.slice(0, 8)}…` : s;
}

function QrDiscountSummary({
  assignedDiscountId,
  appliedDiscountId,
  checkPropertyDiscountId,
  storedAppliedId,
  groupIndex,
  propertyId,
}) {
  const assignedIsGlobal = assignedDiscountId && groupIndex?.isGlobalId(assignedDiscountId);
  const storedLooksGlobal = storedAppliedId && groupIndex?.isGlobalId(storedAppliedId);
  const branchMembers = assignedIsGlobal
    ? groupIndex.getMembersForGlobal(assignedDiscountId)
    : [];
  const groupPropertyDiscountId = resolveGroupPropertyDiscountId(
    assignedDiscountId,
    propertyId,
    groupIndex,
  );
  const wrongStoredGlobal = Boolean(storedLooksGlobal);
  const checkDiffersFromGroup = Boolean(
    groupPropertyDiscountId
    && checkPropertyDiscountId
    && checkPropertyDiscountId.toLowerCase() !== groupPropertyDiscountId.toLowerCase(),
  );
  const storedDiffersFromGroup = Boolean(
    groupPropertyDiscountId
    && storedAppliedId
    && !storedLooksGlobal
    && String(storedAppliedId).toLowerCase() !== groupPropertyDiscountId.toLowerCase(),
  );

  return (
    <section className="qr-discount-summary" aria-label="Simpra indirim ID">
      <p className="qr-discount-summary-title">Simpra indirim</p>
      <dl className="qr-discount-summary-grid">
        <div>
          <dt>Atanan (kullanıcı — global)</dt>
          <dd>
            {assignedDiscountId ? (
              <code className="qr-mono" title={assignedDiscountId}>{assignedDiscountId}</code>
            ) : (
              <span className="qr-customer-empty">—</span>
            )}
          </dd>
        </div>
        {propertyId ? (
          <div>
            <dt>Property ID (şube)</dt>
            <dd>
              <code className="qr-mono" title={propertyId}>{propertyId}</code>
            </dd>
          </div>
        ) : null}
        <div>
          <dt>Uygulanan property indirim ID (grup)</dt>
          <dd>
            {appliedDiscountId ? (
              <code className="qr-mono qr-applied-discount" title={appliedDiscountId}>
                {appliedDiscountId}
              </code>
            ) : (
              <span className="qr-customer-empty">Henüz kaydedilmedi</span>
            )}
          </dd>
        </div>
        {checkPropertyDiscountId ? (
          <div>
            <dt>Check’te görünen property indirim ID</dt>
            <dd>
              <code className="qr-mono" title={checkPropertyDiscountId}>{checkPropertyDiscountId}</code>
            </dd>
          </div>
        ) : null}
      </dl>
      {branchMembers.length > 0 ? (
        <div className="qr-discount-branches">
          <p className="qr-discount-branches-title">Global anahtar → property indirim ID’leri</p>
          <ul className="qr-discount-branches-list">
            {branchMembers.map((m) => {
              const active = propertyId
                && m.propertyId.toLowerCase() === String(propertyId).toLowerCase();
              return (
                <li
                  key={`${m.propertyId}-${m.propertyDiscountId}`}
                  className={active ? 'qr-discount-branches-item--active' : undefined}
                >
                  <span className="qr-discount-branches-shop">
                    {m.shopLabel || 'Şube'}
                  </span>
                  <code className="qr-mono qr-discount-branches-property" title={m.propertyId}>
                    {m.propertyId}
                  </code>
                  <code className="qr-mono qr-discount-branches-discount" title={m.propertyDiscountId}>
                    {m.propertyDiscountId}
                  </code>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
      {wrongStoredGlobal ? (
        <p className="qr-discount-summary-note qr-discount-summary-note--warn">
          Kayıtlı uygulanan ID global görünüyor. <code>applied_discount_id</code> property indirim UUID olmalı (şube property ID değil).
        </p>
      ) : null}
      {storedDiffersFromGroup ? (
        <p className="qr-discount-summary-note qr-discount-summary-note--warn">
          Kayıtlı property indirim ID gruptaki şube eşlemesiyle uyuşmuyor. Gruptaki ID’yi kaydedin.
        </p>
      ) : null}
      {checkDiffersFromGroup ? (
        <p className="qr-discount-summary-note">
          Simpra check’te farklı bir property indirim ID görünüyor. Apply için indirim grubundaki ID kullanılmalı; check’teki eski/farklı indirim olabilir.
        </p>
      ) : null}
    </section>
  );
}

export function SimpraCheckDetailPanel({
  checkId,
  cacheEntry,
  onLoad,
  assignedDiscountId,
  appliedDiscountId,
  storedAppliedId,
  groupIndex,
  propertyId,
  qrRowId,
  onAppliedSaved,
}) {
  const requestedRef = useRef(null);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState('');
  const [syncMsg, setSyncMsg] = useState('');

  useEffect(() => {
    if (!checkId || cacheEntry?.data || cacheEntry?.loading) return;
    if (requestedRef.current === checkId) return;
    requestedRef.current = checkId;
    onLoad(checkId, propertyId);
  }, [checkId, cacheEntry, onLoad, propertyId]);

  if (!checkId) {
    return (
      <div className="qr-check-detail qr-check-detail--error">
        Bu siparişte Simpra check ID yok.
      </div>
    );
  }

  if (cacheEntry?.loading) {
    return (
      <div className="qr-check-detail qr-check-detail--loading">
        <span className="qr-check-detail-spinner" aria-hidden />
        Simpra’dan check yükleniyor…
      </div>
    );
  }

  if (cacheEntry?.error) {
    return (
      <div className="qr-check-detail qr-check-detail--error" role="alert">
        Simpra: {cacheEntry.error}
      </div>
    );
  }

  const check = cacheEntry?.data;
  if (!check) return null;

  const checkPropertyDiscountId = pickPropertyDiscountIdFromCheck(check);
  const groupPropertyDiscountId = resolveGroupPropertyDiscountId(
    assignedDiscountId,
    propertyId || check.property_id,
    groupIndex,
  );
  const storedLooksGlobal = storedAppliedId && groupIndex?.isGlobalId(storedAppliedId);
  const needsSync = Boolean(
    groupPropertyDiscountId
    && (
      !storedAppliedId
      || storedLooksGlobal
      || String(storedAppliedId).toLowerCase() !== groupPropertyDiscountId.toLowerCase()
    ),
  );

  const syncAppliedFromGroup = async () => {
    if (!groupPropertyDiscountId) return;
    setSyncing(true);
    setSyncError('');
    setSyncMsg('');
    try {
      const inputId = assignedDiscountId || groupPropertyDiscountId;
      const res = await callEdgeFunction('record-applied-discount', {
        method: 'POST',
        body: {
          checkId,
          qrId: qrRowId,
          discountId: inputId,
        },
      });
      const savedId = res?.applied_discount_id || groupPropertyDiscountId;
      onAppliedSaved?.(qrRowId, savedId);
      setSyncMsg(`Gruptaki property indirim ID kaydedildi: ${shortId(savedId)}`);
    } catch (e) {
      setSyncError(e?.message || 'Uygulanan indirim kaydedilemedi.');
    } finally {
      setSyncing(false);
    }
  };

  const summary = check.summary || {};
  const items = Array.isArray(check.check_items) ? check.check_items : [];
  const payments = Array.isArray(check.check_payments) ? check.check_payments : [];
  const discounts = Array.isArray(check.check_discounts) ? check.check_discounts : [];

  return (
    <div className="qr-check-detail">
      <QrDiscountSummary
        assignedDiscountId={assignedDiscountId}
        appliedDiscountId={appliedDiscountId}
        checkPropertyDiscountId={checkPropertyDiscountId}
        storedAppliedId={storedAppliedId}
        groupIndex={groupIndex}
        propertyId={propertyId || check.property_id}
      />
      {needsSync ? (
        <div className="qr-discount-sync">
          <p className="qr-discount-sync-text">
            Bu şube için gruptaki property indirim ID:{' '}
            <code className="qr-mono">{groupPropertyDiscountId}</code>
          </p>
          <button
            type="button"
            className="qr-discount-sync-btn"
            onClick={syncAppliedFromGroup}
            disabled={syncing}
          >
            {syncing ? 'Kaydediliyor…' : 'Gruptaki property indirim ID’yi kaydet'}
          </button>
          {syncError ? <p className="qr-discount-sync-error" role="alert">{syncError}</p> : null}
          {syncMsg ? <p className="qr-discount-sync-ok">{syncMsg}</p> : null}
        </div>
      ) : null}
      <div className="qr-check-detail-head">
        <div>
          <p className="qr-check-detail-kicker">Simpra check</p>
          <p className="qr-check-detail-title">
            #{check.sequential_id ?? check.sequence ?? '—'}
            <span className="qr-check-detail-status">{checkStatusLabel(check.status)}</span>
          </p>
          <p className="qr-check-detail-id qr-mono" title={check.id}>{check.id}</p>
        </div>
        <div className="qr-check-detail-total">
          <span className="qr-check-detail-total-label">Toplam</span>
          <span className="qr-check-detail-total-value">{formatSimpraMoney(summary.total_amount)}</span>
        </div>
      </div>

      <dl className="qr-check-detail-grid">
        <div><dt>İş günü</dt><dd>{check.business_date || '—'}</dd></div>
        <div><dt>Kapanış</dt><dd>{unixToLocale(check.closed_at)}</dd></div>
        <div><dt>Masa</dt><dd>{check.table?.name || '—'}</dd></div>
        <div><dt>Personel</dt><dd>{formatPerson(check.employee)}</dd></div>
        <div><dt>Gelir merkezi</dt><dd>{check.revenue_center?.name || '—'}</dd></div>
        <div><dt>Kanal</dt><dd>{check.sales_channel?.name || '—'}</dd></div>
        <div><dt>Ödenen</dt><dd>{formatSimpraMoney(summary.paid_amount)}</dd></div>
        <div><dt>Ödenmemiş</dt><dd>{formatSimpraMoney(summary.unpaid_amount)}</dd></div>
        <div><dt>İndirim</dt><dd>{formatSimpraMoney(summary.discounts_amount)}</dd></div>
        <div><dt>Kalem</dt><dd>{items.length}</dd></div>
        <div><dt>Ödeme</dt><dd>{payments.length}</dd></div>
        <div><dt>İndirim satırı</dt><dd>{discounts.length}</dd></div>
      </dl>

      {items.length > 0 ? (
        <div className="qr-check-items">
          <p className="qr-check-items-title">Kalemler</p>
          <ul className="qr-check-items-list">
            {items.slice(0, 8).map((item) => (
              <li key={item.id}>
                <span className="qr-check-item-qty">{item.quantity ?? '1'}×</span>
                <span className="qr-check-item-name">
                  {item.item?.name || item.name || item.notes?.[0] || 'Kalem'}
                </span>
                <span className="qr-check-item-status">{item.payment_status || item.status || ''}</span>
              </li>
            ))}
            {items.length > 8 ? (
              <li className="qr-check-items-more">+{items.length - 8} kalem daha</li>
            ) : null}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
